import { Alert, Autocomplete, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, List, ListItem, ListItemText, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ErrorState, LoadingState, PageHeader, PermissionGate } from "../components";
import { formatRelative } from "../i18n/format";
import help from "./HELP.md?raw";
import { useApiKeys, useCreateKey, useKeyAction, useSaveServiceAccount, useServiceAccounts, type ApiKey } from "./useData";

function SecretReveal({ secret, onClose }: { secret: string; onClose: () => void }) {
  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>Copy your new key</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>This is the only time the full key is shown. Store it in your secret manager now.</Alert>
        <TextField value={secret} fullWidth slotProps={{ input: { readOnly: true } }} onFocus={(e) => e.target.select()} />
        <Typography variant="body2" sx={{ mt: 2 }}>Use it as <code>Authorization: Bearer {"<key>"}</code>. Docs: <a href="/api/docs/" target="_blank" rel="noreferrer">/api/docs/</a></Typography>
      </DialogContent>
      <DialogActions><Button variant="contained" onClick={() => { void navigator.clipboard?.writeText(secret); onClose(); }}>Copy & close</Button></DialogActions>
    </Dialog>
  );
}

function NewKeyDialog({ serviceAccountId, onClose, onCreated }: { serviceAccountId: string | null; onClose: () => void; onCreated: (k: ApiKey) => void }) {
  const { me } = useAuth();
  const create = useCreateKey();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [expires, setExpires] = useState("");
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>{serviceAccountId ? "New organization key" : "New personal access token"}</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
        {error && <ErrorState message={error} />}
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus sx={{ mt: 1 }} helperText="Where will it be used? e.g. 'Shop-floor kiosk', 'Zapier'" />
        <Autocomplete multiple options={me?.permissions ?? []} value={scopes} onChange={(_, v) => setScopes(v)} renderInput={(p) => <TextField {...p} label="Limit to permissions (empty = all of the holder's)" />} />
        <TextField label="Expires" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} InputLabelProps={{ shrink: true }} helperText="Recommended for integrations you might forget about." />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!name || create.isPending} onClick={async () => {
          try { onCreated(await create.mutateAsync({ name, service_account: serviceAccountId, scopes, expires_at: expires ? new Date(expires).toISOString() : null })); onClose(); }
          catch (err) { setError((err as Error).message); }
        }}>Create key</Button>
      </DialogActions>
    </Dialog>
  );
}

export function ApiKeys() {
  const { hasPermission } = useAuth();
  const keys = useApiKeys();
  const accounts = useServiceAccounts();
  const keyAction = useKeyAction();
  const saveAccount = useSaveServiceAccount();
  const [creating, setCreating] = useState<{ sa: string | null } | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [newAccount, setNewAccount] = useState("");
  const canManage = hasPermission("accounts.api_key.view");

  if (keys.isLoading) return <LoadingState />;
  if (keys.isError) return <ErrorState message={keys.error.message} onRetry={() => void keys.refetch()} />;
  const render = (list: ApiKey[]) => (
    <List dense>
      {list.length === 0 && <ListItem><ListItemText secondary="No keys yet." /></ListItem>}
      {list.map((k) => (
        <ListItem key={k.id} secondaryAction={k.is_valid && (
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={async () => { const r = await keyAction.mutateAsync({ id: k.id, action: "rotate" }); if (r?.key) setSecret(r.key); }}>Rotate</Button>
            <Button size="small" color="error" onClick={() => { if (confirm(`Revoke ${k.name}?`)) keyAction.mutate({ id: k.id, action: "revoke" }); }}>Revoke</Button>
          </Stack>)}>
          <ListItemText primary={<>{k.name} <code style={{ marginLeft: 8 }}>{k.display_prefix}</code> {!k.is_valid && <Chip label={k.revoked_at ? "revoked" : "expired"} size="small" sx={{ ml: 1 }} />}</>}
            secondary={`${k.scopes.length ? `${k.scopes.length} scopes` : "all permissions"} · last used ${k.last_used_at ? formatRelative(k.last_used_at) : "never"}${k.expires_at ? ` · expires ${k.expires_at.slice(0, 10)}` : ""}`} />
        </ListItem>
      ))}
    </List>
  );
  return (
    <>
      <PageHeader help={help} title="API keys" subtitle="Credentials for integrations and scripts. Keys inherit the permissions of their holder." crumbs={[{ label: "Settings" }, { label: "API keys" }]} />
      {keyAction.isError && <ErrorState message={keyAction.error.message} />}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card><CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography variant="subtitle2">Personal access tokens</Typography><Button size="small" variant="outlined" onClick={() => setCreating({ sa: null })}>New token</Button></Stack>
            {render(keys.data!.filter((k) => k.is_personal))}
          </CardContent></Card>
        </Grid>
        {canManage && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card><CardContent>
              <Typography variant="subtitle2" gutterBottom>Service accounts</Typography>
              <PermissionGate permission="accounts.service_account.create">
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <TextField size="small" placeholder="New service account name" value={newAccount} onChange={(e) => setNewAccount(e.target.value)} />
                  <Button variant="outlined" disabled={!newAccount} onClick={async () => { await saveAccount.mutateAsync({ name: newAccount }); setNewAccount(""); }}>Add</Button>
                </Stack>
              </PermissionGate>
              {accounts.data?.map((sa) => (
                <Card key={sa.id} variant="outlined" sx={{ mb: 1 }}><CardContent sx={{ py: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight={600}>{sa.name} {!sa.is_active && <Chip label="disabled" size="small" />}</Typography>
                    <Stack direction="row" spacing={1}>
                      <PermissionGate permission="accounts.api_key.create"><Button size="small" disabled={!sa.is_active} onClick={() => setCreating({ sa: sa.id })}>New key</Button></PermissionGate>
                      <PermissionGate permission="accounts.service_account.change"><Button size="small" onClick={() => saveAccount.mutate({ id: sa.id, is_active: !sa.is_active })}>{sa.is_active ? "Disable" : "Enable"}</Button></PermissionGate>
                    </Stack>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">Roles are assigned under Members (the service account appears as a member).</Typography>
                  {render(keys.data!.filter((k) => k.service_account === sa.id))}
                </CardContent></Card>
              ))}
            </CardContent></Card>
          </Grid>
        )}
      </Grid>
      {creating && <NewKeyDialog serviceAccountId={creating.sa} onClose={() => setCreating(null)} onCreated={(k) => k.key && setSecret(k.key)} />}
      {secret && <SecretReveal secret={secret} onClose={() => setSecret(null)} />}
    </>
  );
}
